import { environmentDataAttribute, useDataAttributes } from './script-attributes'
import { getIdentityProviderDataAttribute, isEntraExternalIdAttribute } from './identity-provider'

/** GM CDN-hosted UAT/preprod sign-in entry (query params supplied at redirect time). */
export const GM_ACCOUNTS_CDN_PREPROD_AUTH_BASE_URL = 'https://accounts-gm-com.cdn-uat.gm.com/auth'

/**
 * GM CDN-hosted UAT/preprod RP-initiated logout (see MyAccount logout integration guide).
 * Query params (e.g. `post_logout_redirect_uri`) are supplied at redirect time.
 */
export const GM_ACCOUNTS_CDN_PREPROD_LOGOUT_BASE_URL = 'https://accounts-gm-com.cdn-uat.gm.com/auth/logout'

/** GM production sign-in entry (query params supplied at redirect time). */
export const GM_ACCOUNTS_CDN_PROD_AUTH_BASE_URL = 'https://accounts.gm.com/auth'

/**
 * GM production RP-initiated logout.
 * Query params (e.g. `post_logout_redirect_uri`) are supplied at redirect time.
 */
export const GM_ACCOUNTS_CDN_PROD_LOGOUT_BASE_URL = 'https://accounts.gm.com/auth/logout'

const DATA_ATTRIBUTES_ENABLED = 'true'

function isHeadlessNaEntraGmAccountsCdnContext(): boolean {
	if (useDataAttributes !== DATA_ATTRIBUTES_ENABLED) {
		return false
	}
	const environment = environmentDataAttribute?.trim()
	if (environment !== 'Preproduction' && environment !== 'Production') {
		return false
	}
	return isEntraExternalIdAttribute(getIdentityProviderDataAttribute())
}

/**
 * GM accounts CDN auth base URL for the current headless Entra context, or null when CDN redirect is not used.
 */
export function getGmAccountsCdnEntraAuthBaseUrl(): string | null {
	if (!isHeadlessNaEntraGmAccountsCdnContext()) {
		return null
	}
	return environmentDataAttribute?.trim() === 'Production'
		? GM_ACCOUNTS_CDN_PROD_AUTH_BASE_URL
		: GM_ACCOUNTS_CDN_PREPROD_AUTH_BASE_URL
}

/**
 * GM accounts CDN logout base URL for the current headless Entra context, or null when CDN redirect is not used.
 */
export function getGmAccountsCdnEntraLogoutBaseUrl(): string | null {
	if (!isHeadlessNaEntraGmAccountsCdnContext()) {
		return null
	}
	return environmentDataAttribute?.trim() === 'Production'
		? GM_ACCOUNTS_CDN_PROD_LOGOUT_BASE_URL
		: GM_ACCOUNTS_CDN_PREPROD_LOGOUT_BASE_URL
}

/**
 * Headless NA Entra preprod/production: OAuth authorize step is started on GM accounts `/auth` instead of MSAL's default redirect.
 */
export function shouldUseGmAccountsCdnEntraLoginRedirect(): boolean {
	return isHeadlessNaEntraGmAccountsCdnContext()
}

/**
 * Same gate as {@link shouldUseGmAccountsCdnEntraLoginRedirect}: headless data attributes, Preproduction or Production, Entra External ID.
 * When true, logout uses GM CDN `/auth/logout` (RP-initiated) instead of MSAL's default end-session redirect.
 */
export function shouldUseGmAccountsCdnEntraLogoutRedirect(): boolean {
	return isHeadlessNaEntraGmAccountsCdnContext()
}
